import { Dispatch, SetStateAction } from 'react';

import { Fade } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import dayjs, { Dayjs } from 'dayjs';
import {
  ServiceType,
  SERVICE_TYPE_LABELS,
} from '../../../../happypaws-shared/src/Services/services';

export interface Step4State {
  availabilityDates: Record<string, boolean>;
  pricingItems: { service: ServiceType; priceMin: number; priceMax: number }[];
  currentMonth: Dayjs;
}

interface StepFourProps {
  currentStep: number;
  step4: Step4State;
  serviceTypes: ServiceType[];
  setStep4: Dispatch<SetStateAction<Step4State>>;
  handleNext: () => void;
  handleBack: () => void;
  isPending: boolean;
  errors: Record<string, string>;
  clearError: (key: string) => void;
}

const StepFour = (props: StepFourProps) => {
  const {
    currentStep,
    step4,
    setStep4,
    handleNext,
    handleBack,
    isPending,
    errors,
    clearError,
    serviceTypes,
  } = props;

  const { availabilityDates, pricingItems, currentMonth } = step4;

  const addPricingItem = () => {
    setStep4((prev) => {
      const usedServices = prev.pricingItems.map((item) => item.service);
      const nextService = serviceTypes.find((s) => !usedServices.includes(s)) ?? serviceTypes[0];
      return {
        ...prev,
        pricingItems: [...prev.pricingItems, { service: nextService, priceMin: 10, priceMax: 50 }],
      };
    });
  };

  const removePricingItem = (index: number) => {
    if (pricingItems.length > 1) {
      setStep4((prev) => ({
        ...prev,
        pricingItems: prev.pricingItems.filter((_, i) => i !== index),
      }));
    }
  };

  const handlePrevMonth = () => {
    setStep4((prev) => ({ ...prev, currentMonth: prev.currentMonth.subtract(1, 'month') }));
  };

  const handleNextMonth = () => {
    setStep4((prev) => ({ ...prev, currentMonth: prev.currentMonth.add(1, 'month') }));
  };

  const handleToggleDate = (date: string) => {
    setStep4((prev) => ({
      ...prev,
      availabilityDates: {
        ...prev.availabilityDates,
        [date]: !prev.availabilityDates[date],
      },
    }));
  };

  const isAvailable = (date: Dayjs): boolean => {
    const dateString = date.format('YYYY-MM-DD');
    return availabilityDates[dateString] ?? false;
  };

  const isCurrentMonth = (date: Dayjs): boolean => {
    return date.month() === currentMonth.month();
  };

  const isToday = (date: Dayjs): boolean => {
    return date.isSame(dayjs(), 'day');
  };

  const getCalendarDays = () => {
    const firstDayOfMonth = currentMonth.startOf('month');
    const lastDayOfMonth = currentMonth.endOf('month');
    const startDate = firstDayOfMonth.startOf('week');
    const endDate = lastDayOfMonth.endOf('week');

    const days: Dayjs[] = [];
    let currentDate = startDate;

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      days.push(currentDate);
      currentDate = currentDate.add(1, 'day');
    }

    return days;
  };

  const calendarDays = getCalendarDays();
  const weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  return (
    <Fade in={currentStep === 4} timeout={600}>
      <Box sx={{ py: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.8rem',
            fontWeight: 700,
            color: '#178B8E',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Define your availability and pricing to make booking easy.
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: '1rem',
            color: '#757575',
            mb: 5,
            textAlign: 'center',
          }}
        >
          Let pet owners know when you're ready to help—and how much your services cost.
        </Typography>

        <Stack spacing={4}>
          {/* Availability Calendar */}
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Set your availability so pet owners know exactly when you're free.
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#178B8E', mb: 3 }}>
              Use the calendar below to mark the days you're available and the days you're not.
            </Typography>

            <Box>
              {/* Month Navigation */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <IconButton onClick={handlePrevMonth} size="small">
                  <NavigateBeforeIcon />
                </IconButton>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mx: 3,
                    minWidth: 150,
                    textAlign: 'center',
                  }}
                >
                  {currentMonth.format('MMMM YYYY')}
                </Typography>

                <IconButton onClick={handleNextMonth} size="small">
                  <NavigateNextIcon />
                </IconButton>
              </Box>

              {/* Weekday Headers */}
              <Grid container spacing={1} sx={{ mb: 1 }}>
                {weekDays.map((day) => (
                  <Grid size={{ xs: 12 / 7 }} key={day}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#5A5A5A' }}>
                        {day}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Calendar Days */}
              <Grid container spacing={1}>
                {calendarDays.map((date, index) => {
                  const available = isAvailable(date);
                  const inCurrentMonth = isCurrentMonth(date);
                  const today = isToday(date);

                  return (
                    <Grid size={{ xs: 12 / 7 }} key={index}>
                      <Box
                        onClick={() => {
                          if (inCurrentMonth) {
                            handleToggleDate(date.format('YYYY-MM-DD'));
                          }
                        }}
                        sx={{
                          aspectRatio: '1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          cursor: available && inCurrentMonth ? 'pointer' : 'default',
                          backgroundColor: !inCurrentMonth
                            ? 'transparent'
                            : available
                              ? '#B2E5E7'
                              : '#E8E8E8',
                          border: today ? '2px solid #178B8E' : 'none',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: available && inCurrentMonth ? '#8FD8DB' : undefined,
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: today ? 700 : 400,
                            color: inCurrentMonth ? '#333' : '#B0B0B0',
                          }}
                        >
                          {date.date()}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Legend */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  mt: 3,
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: '#B2E5E7',
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
                    Available
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: '#E8E8E8',
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
                    Unavailable
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: '#178B8E',
                  }}
                />
                <Typography sx={{ fontSize: '0.875rem' }}>Available</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: '#B8D8D8',
                  }}
                />
                <Typography sx={{ fontSize: '0.875rem' }}>Unavailable</Typography>
              </Box>
            </Box>
          </Box>

          {/* Pricing */}
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Define how you charge for your services.
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#178B8E', mb: 3 }}>
              Select whether you charge hourly or per service, and set the rates for each option.
            </Typography>

            <Stack spacing={2}>
              {pricingItems.map((item, index) => {
                const rowAvailable = serviceTypes.filter(
                  (s) => s === item.service || !pricingItems.some((p, i) => i !== index && p.service === s)
                );
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: '#FAFAFA',
                    }}
                  >
                    {/* Left: service selector + price labels */}
                    <Box sx={{ flex: '0 0 220px' }}>
                      <FormControl fullWidth size="small">
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 0.5, color: 'text.secondary' }}>
                          Service
                        </Typography>
                        <Select
                          value={item.service}
                          onChange={(e) => {
                            setStep4((prev) => {
                              const newItems = [...prev.pricingItems];
                              newItems[index] = { ...newItems[index], service: e.target.value as ServiceType };
                              return { ...prev, pricingItems: newItems };
                            });
                            clearError('pricingItems');
                          }}
                          displayEmpty
                          renderValue={(value) =>
                            value ? (
                              SERVICE_TYPE_LABELS[value as ServiceType]
                            ) : (
                              <span style={{ color: '#9e9e9e' }}>Select service</span>
                            )
                          }
                          sx={{ backgroundColor: '#FFFFFF', borderRadius: '8px' }}
                        >
                          {rowAvailable.map((s) => (
                            <MenuItem key={s} value={s}>
                              {SERVICE_TYPE_LABELS[s]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Min price</Typography>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#178B8E' }}>
                            ${item.priceMin}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Max price</Typography>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#178B8E' }}>
                            ${item.priceMax}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Right: range slider */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 1, color: 'text.secondary' }}>
                        Price range ($ / hour)
                      </Typography>
                      <Slider
                        value={[item.priceMin, item.priceMax]}
                        onChange={(_, value) => {
                          const [min, max] = value as number[];
                          setStep4((prev) => {
                            const newItems = [...prev.pricingItems];
                            newItems[index] = { ...newItems[index], priceMin: min, priceMax: max };
                            return { ...prev, pricingItems: newItems };
                          });
                          clearError('pricingItems');
                        }}
                        min={0}
                        max={300}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `$${v}`}
                        sx={{
                          color: '#178B8E',
                          '& .MuiSlider-thumb': { backgroundColor: '#178B8E' },
                          '& .MuiSlider-track': { backgroundColor: '#178B8E' },
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">$0</Typography>
                        <Typography variant="caption" color="text.secondary">$300</Typography>
                      </Box>
                    </Box>

                    {pricingItems.length > 1 && (
                      <IconButton
                        onClick={() => removePricingItem(index)}
                        size="small"
                        sx={{
                          color: '#B0B0B0',
                          '&:hover': { color: '#D32F2F', backgroundColor: '#FFEBEE' },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                  </Box>
                );
              })}

              {errors.pricingItems && (
                <Typography variant="caption" color="error">
                  {errors.pricingItems}
                </Typography>
              )}
              {pricingItems.length < serviceTypes.length && (
                <Button
                  onClick={addPricingItem}
                  sx={{
                    color: '#178B8E',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    alignSelf: 'flex-start',
                    '&:hover': {
                      textDecoration: 'underline',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Add more
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleBack}
            sx={{
              borderColor: '#178B8E',
              color: '#178B8E',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#0F6164',
                backgroundColor: '#F0F9F9',
              },
            }}
          >
            Previous
          </Button>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleNext}
            disabled={isPending}
            sx={{
              backgroundColor: '#178B8E',
              color: '#FFFFFF',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#0F6164',
              },
            }}
          >
            {isPending ? 'Saving...' : 'Finish'}
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default StepFour;
